"use client";

import { useState, useMemo, memo } from "react";
import CTAButton from "@/app/ui/atomic/content/CTAButton";

interface Question {
  id: string;
  text: string;
  options: { label: string; score: number }[];
}

const questions: Question[] = [
  {
    id: "idea",
    text: "How developed is your business idea?",
    options: [
      { label: "Just a concept", score: 1 },
      { label: "I have a basic plan", score: 2 },
      { label: "Detailed business plan ready", score: 3 },
    ],
  },
  {
    id: "team",
    text: "Do you have potential co-founders or team members?",
    options: [
      { label: "No, I'm solo for now", score: 1 },
      { label: "I know some interested people", score: 2 },
      { label: "Yes, committed team members", score: 3 },
    ],
  },
  {
    id: "experience",
    text: "What's your experience in the industry?",
    options: [
      { label: "New to this industry", score: 1 },
      { label: "Some relevant experience", score: 2 },
      { label: "Expert with years of experience", score: 3 },
    ],
  },
  {
    id: "funding",
    text: "How prepared are you to run a crowdfunding campaign?",
    options: [
      { label: "New to crowdfunding", score: 1 },
      { label: "I understand the basics", score: 2 },
      { label: "Ready to engage supporters", score: 3 },
    ],
  },
  {
    id: "commitment",
    text: "How committed are you to democratic ownership?",
    options: [
      { label: "Still exploring options", score: 1 },
      { label: "Strongly prefer democratic models", score: 2 },
      { label: "100% committed to shared ownership", score: 3 },
    ],
  },
];

interface Result {
  level: "exploring" | "developing" | "ready";
  title: string;
  description: string;
  nextStep: string;
  ctaText: string;
  ctaLink: string;
}

const getResult = (score: number): Result => {
  if (score >= 13) {
    return {
      level: "ready",
      title: "You're Ready to Launch!",
      description: "Your preparation puts you in a strong position to start a democratic workplace. You have the foundation needed to begin the FlockTank process.",
      nextStep: "Submit your proposal and get expert feedback within 2 weeks.",
      ctaText: "Get Started",
      ctaLink: "/get-started/entrepreneurs",
    };
  } else if (score >= 9) {
    return {
      level: "developing",
      title: "You're on the Right Track",
      description: "You've made good progress! A few more steps will strengthen your foundation before launching.",
      nextStep: "Connect with us to discuss how we can help fill the gaps.",
      ctaText: "Get in Touch",
      ctaLink: "/connect/contact",
    };
  } else {
    return {
      level: "exploring",
      title: "Great Start!",
      description: "Every successful organization started with curiosity. You're in the early stages, and that's okay!",
      nextStep: "Learn more about what it takes to start a democratic workplace.",
      ctaText: "Learn More",
      ctaLink: "/about-us",
    };
  }
};

interface QuizQuestionProps {
  question: Question;
  questionNumber: number;
  totalQuestions: number;
  progress: number;
  onAnswer: (score: number) => void;
  onBack: () => void;
  canGoBack: boolean;
}

const QuizQuestion = memo(function QuizQuestion({
  question,
  questionNumber,
  totalQuestions,
  progress,
  onAnswer,
  onBack,
  canGoBack,
}: QuizQuestionProps) {
  return (
    <div className="theme-card-alt rounded-xl p-6 shadow-sm">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-['Font-Bold'] text-(--text-muted)">
            Question {questionNumber} of {totalQuestions}
          </span>
          <span className="text-sm text-(--brand) font-['Font-Bold']">
            {Math.round(progress)}% Complete
          </span>
        </div>
        <div className="w-full h-2 bg-(--bg-secondary) rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-(--gold) to-(--brand) transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <h3 className="font-['Font-Bold'] text-xl text-(--text-headline) mb-6 text-center">
        {question.text}
      </h3>

      <div className="space-y-3">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => onAnswer(option.score)}
            className="w-full p-4 rounded-lg border-2 border-(--border-light) text-left transition-all duration-200 hover:border-(--brand) hover:bg-(--brand)/5 focus:outline-none focus:ring-2 focus:ring-(--brand) group"
          >
            <span className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full bg-(--purple)/10 flex items-center justify-center text-sm font-['Font-Bold'] text-(--purple-accent) group-hover:bg-(--brand) group-hover:text-(--text-on-pink) transition-colors">
                {String.fromCharCode(65 + index)}
              </span>
              <span className="text-(--text-secondary) group-hover:text-(--text-headline) transition-colors">
                {option.label}
              </span>
            </span>
          </button>
        ))}
      </div>

      {canGoBack && (
        <button
          onClick={onBack}
          className="mt-4 text-sm text-(--text-muted) hover:text-(--brand) transition-colors"
        >
          &larr; Previous question
        </button>
      )}
    </div>
  );
});

interface QuizResultProps {
  result: Result;
  onReset: () => void;
}

const QuizResult = memo(function QuizResult({ result, onReset }: QuizResultProps) {
  return (
    <div className="theme-card-alt rounded-xl p-6 shadow-sm">
      <div className="text-center mb-6">
        <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
          result.level === "ready" ? "bg-(--gold)" : 
          result.level === "developing" ? "bg-(--brand)" : "bg-(--purple)"
        }`}>
          <svg className={`w-8 h-8 ${result.level === "ready" ? "text-(--text-on-gold)" : result.level === "developing" ? "text-(--text-on-pink)" : "text-(--text-on-purple)"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            {result.level === "ready" ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            ) : result.level === "developing" ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            )}
          </svg>
        </div>
        <h3 className="font-['Font-Bold'] text-2xl text-(--text-headline) mb-2">{result.title}</h3>
        <p className="text-(--text-muted) mb-4">{result.description}</p>
        <p className="text-sm font-medium text-(--text-secondary) mb-6">
          <span className="text-(--gold-accent)">Next step:</span> {result.nextStep}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <CTAButton href={result.ctaLink} variant="gold" size="sm">
            {result.ctaText}
          </CTAButton>
          <button
            onClick={onReset}
            className="px-4 py-2 text-sm font-['Font-Bold'] text-(--text-muted) hover:text-(--brand) transition-colors"
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
});

export default function CoopReadinessQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [showResult, setShowResult] = useState(false);

  const handleAnswer = (score: number) => {
    const question = questions[currentQuestion]!;
    setAnswers(prev => ({ ...prev, [question.id]: score }));
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setShowResult(true);
    }
  };

  const handleBack = () => {
    setCurrentQuestion(prev => prev - 1);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResult(false);
  };

  const totalScore = useMemo(
    () => Object.values(answers).reduce((sum, score) => sum + score, 0),
    [answers]
  );

  const result = useMemo(() => getResult(totalScore), [totalScore]);

  const progress = useMemo(
    () => ((currentQuestion + (showResult ? 1 : 0)) / questions.length) * 100,
    [currentQuestion, showResult]
  );

  if (showResult) {
    return <QuizResult result={result} onReset={resetQuiz} />;
  }

  return (
    <QuizQuestion
      question={questions[currentQuestion]!}
      questionNumber={currentQuestion + 1}
      totalQuestions={questions.length}
      progress={progress}
      onAnswer={handleAnswer}
      onBack={handleBack}
      canGoBack={currentQuestion > 0}
    />
  );
}
